export const CONTROL_HEALTH_ORDER = [
  { value: 1, label: "Critical" },
  { value: 2, label: "Ill" },
  { value: 3, label: "Moderate" },
  { value: 4, label: "Good" },
  { value: 5, label: "Healthy" },
  { value: null, label: "Not Measured" },
];

export const CR_STATUS_ORDER = [
  { label: "Not Started", ids: [10401] },
  { label: "In Review", ids: [10415, 10421] },
  { label: "Completed", ids: [10403] },
  { label: "Not Applicable", ids: [10417] },
];

export const FINDING_STATUS_ORDER = [
  { label: "Open", ids: [10401] },
  { label: "In Progress", ids: [10402] },
  { label: "For Review", ids: [10419, 10415, 10414] },
  { label: "Partial", ids: [10406] },
  { label: "Completed", ids: [10403] },
];

export const CAPA_STATUS_ORDER = [
  { label: "Open", ids: [10401] },
  { label: "In Progress", ids: [10402] },
  { label: "In Review", ids: [10415, 10421] },
  { label: "Completed", ids: [10403] },
  { label: "Waiting For Approval", ids: [10408] },
  { label: "For Review", ids: [10419] },
];

export const KPI_RESULT_ORDER = ["Pass", "Fail"];
export const KPI_TEAM_RESULT_ORDER = ["Pass", "Fail", "No Instances"];

export const statusCodeOf = (item: any) => Number(item?.id ?? item?.status ?? item?.statusCode);

export const sumStatusTotals = (statusItems: any[], ids: number[]) =>
  (statusItems || []).reduce((sum: number, item: any) => {
    return ids.includes(statusCodeOf(item)) ? sum + (Number(item.total) || 0) : sum;
  }, 0);

export const rollupLocations = (
  locations: any[],
  statusOrder?: Array<{ label: string; ids: number[] }>
) =>
  ((locations || []) as any[])
    .map((location: any) => {
      const teams = location.teams || location.Teams || [];
      const status = (statusOrder || []).map((entry) => ({
        label: entry.label,
        total: teams.reduce(
          (sum: number, team: any) => sum + sumStatusTotals(team.status, entry.ids),
          0
        ),
      }));
      const totalTasks = statusOrder?.length
        ? status.reduce((sum: number, item: { total: number }) => sum + item.total, 0)
        : teams.reduce((sum: number, team: any) => sum + (Number(team.totalTasks) || 0), 0);
      return {
        locationId: location.locationId,
        locationName: location.locationName || "Unknown",
        totalTasks,
        status,
      };
    })
    .filter((location: { totalTasks: number }) => location.totalTasks > 0);

export const stackedStatusSeries = (
  rows: any[],
  statusOrder: Array<{ label: string; ids?: number[] }> | string[]
): { name: string; data: number[] }[] => {
  const entries = statusOrder.map((entry) =>
    typeof entry === "string" ? { label: entry, ids: undefined as number[] | undefined } : entry
  );
  return entries
    .map((entry) => ({
      name: entry.label,
      data: rows.map((row: any) => {
        const byCode = entry.ids?.length ? sumStatusTotals(row.status, entry.ids) : 0;
        const match = (row.status || []).find((item: any) => item.label === entry.label);
        const byLabel = Number(match?.total) || 0;
        return byCode || byLabel;
      }),
    }))
    .filter((series) => series.data.some((value: number) => value > 0));
};

export const controlHealthLabel = (value: any): string => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric >= 1 && numeric <= 5) {
    const match = CONTROL_HEALTH_ORDER.find((entry) => entry.value === numeric);
    return match?.label || "Not Measured";
  }
  return "Not Measured";
};

export const flattenControlHealth = (controlTeams: any[]) => {
  const rows: Array<{
    locationId: string;
    locationName: string;
    teamId: string;
    teamName: string;
    controlHealth: number | null;
    label: string;
  }> = [];
  ((controlTeams || []) as any[]).forEach((location: any) => {
    ((location.teams || location.Teams || []) as any[]).forEach((team: any) => {
      ((team.controlResult || []) as any[]).forEach((result: any) => {
        const raw = result.controlHealth ?? result.control_health;
        const numeric = Number(raw);
        const controlHealth =
          raw === undefined || raw === null || raw === "" || !Number.isFinite(numeric) || numeric < 1 || numeric > 5
            ? null
            : numeric;
        rows.push({
          locationId: String(location.locationId || ""),
          locationName: location.locationName || "Unknown",
          teamId: String(team.teamId || ""),
          teamName: team.teamName || "Unknown",
          controlHealth,
          label: controlHealthLabel(controlHealth),
        });
      });
    });
  });
  return rows;
};

export const countControlHealth = (rows: Array<{ label: string }>) =>
  CONTROL_HEALTH_ORDER.map((entry) => ({
    label: entry.label,
    total: rows.filter((row) => row.label === entry.label).length,
  })).filter((item) => item.total > 0);

export const groupControlHealthBy = (
  rows: Array<{ label: string; [key: string]: any }>,
  idKey: string,
  nameKey: string
) => {
  const buckets = new Map<string, { id: string; name: string; status: Array<{ label: string; total: number }> }>();
  rows.forEach((row) => {
    const id = String(row[idKey] || "");
    if (!id) {
      return;
    }
    if (!buckets.has(id)) {
      buckets.set(id, {
        id,
        name: row[nameKey] || "Unknown",
        status: CONTROL_HEALTH_ORDER.map((entry) => ({ label: entry.label, total: 0 })),
      });
    }
    const bucket = buckets.get(id);
    const status = bucket?.status.find((item) => item.label === row.label);
    if (status) {
      status.total += 1;
    }
  });
  return Array.from(buckets.values()).filter((bucket) =>
    bucket.status.some((item) => item.total > 0)
  );
};
